import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { fetchRetailerBestOffers, fetchRetailerDiscountFilterOptions } from '../../../services/Http/Api';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import { FaSearch, FaSortAmountDown, FaSortAmountUp, FaTimes, FaCalendarAlt, FaBuilding, FaTruck } from 'react-icons/fa';
import { useCurrency } from '../../../context/CurrencyProvider';
import { SearchableSelect } from '../../../shared/layout/forms/AppSelect';
import { formatDiscountPercent, resolveDiscountedAmounts } from '../../../utils/discountDisplay';

const RetailerBestOffers = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount, getCurrencyById } = useCurrency();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterOptions, setFilterOptions] = useState({ manufacturers: [], distributors: [] });

    // Pagination state
    const [meta, setMeta] = useState({ current_page: 1, per_page: 20, total: 0, last_page: 1 });

    // Filter state
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedManufacturer, setSelectedManufacturer] = useState(null);
    const [selectedDistributor, setSelectedDistributor] = useState(null);
    const [minDiscount, setMinDiscount] = useState(null);

    // Sort state
    const [sortBy, setSortBy] = useState('discount_value');
    const [sortDirection, setSortDirection] = useState('desc');

    // Detail modal state
    const [selectedOffer, setSelectedOffer] = useState(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Load filter options on mount
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const options = await fetchRetailerDiscountFilterOptions();
                setFilterOptions(options || { manufacturers: [], distributors: [] });
            } catch (error) {
                console.error('Error loading filter options:', error);
            }
        };
        loadFilterOptions();
    }, []);

    // Load offers
    const loadOffers = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: meta.per_page,
                sort_by: sortBy,
                sort_direction: sortDirection,
            };

            if (debouncedSearch) params.search = debouncedSearch;
            if (selectedManufacturer) params.manufacturer_id = selectedManufacturer.value;
            if (selectedDistributor) params.distributor_id = selectedDistributor.value;
            if (minDiscount) params.min_discount = minDiscount.value;

            const response = await fetchRetailerBestOffers(params);
            setOffers(response.data || []);
            setMeta(response.meta || meta);
        } catch (error) {
            toast.error(t('RetailerBestOffers.error'));
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, selectedManufacturer, selectedDistributor, minDiscount, sortBy, sortDirection, meta.per_page, t]);

    // Reload when filters change
    useEffect(() => {
        loadOffers(1);
    }, [debouncedSearch, selectedManufacturer, selectedDistributor, minDiscount, sortBy, sortDirection]);

    const handlePageChange = (page) => {
        if (page < 1 || page > meta.last_page) return;
        loadOffers(page);
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(field);
            setSortDirection('desc');
        }
    };

    const manufacturerOptions = useMemo(() =>
        filterOptions.manufacturers.map(m => ({ value: m.id, label: m.name })),
        [filterOptions.manufacturers]
    );

    const distributorOptions = useMemo(() =>
        filterOptions.distributors.map(d => ({ value: d.id, label: d.name })),
        [filterOptions.distributors]
    );

    const minDiscountOptions = [
        { value: 5, label: '5%+' },
        { value: 10, label: '10%+' },
        { value: 15, label: '15%+' },
        { value: 20, label: '20%+' },
        { value: 25, label: '25%+' },
        { value: 30, label: '30%+' },
        { value: 50, label: '50%+' },
    ];

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: 42,
            borderColor: 'rgba(130, 22, 146, 0.3)',
            '&:hover': { borderColor: 'rgba(130, 22, 146, 0.5)' },
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? 'rgba(130, 22, 146, 0.1)' : state.isFocused ? 'rgba(130, 22, 146, 0.05)' : 'white',
            color: '#333',
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999,
        }),
    };

    const renderPagination = () => {
        if (meta.last_page <= 1) return null;

        const pages = [];
        const showPages = 5;
        let startPage = Math.max(1, meta.current_page - Math.floor(showPages / 2));
        let endPage = Math.min(meta.last_page, startPage + showPages - 1);

        if (endPage - startPage + 1 < showPages) {
            startPage = Math.max(1, endPage - showPages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <Pagination>
                <PageButton
                    onClick={() => handlePageChange(meta.current_page - 1)}
                    disabled={meta.current_page === 1}
                >
                    {t('common.previous')}
                </PageButton>
                {startPage > 1 && (
                    <>
                        <PageNumber onClick={() => handlePageChange(1)}>1</PageNumber>
                        {startPage > 2 && <PageEllipsis>...</PageEllipsis>}
                    </>
                )}
                {pages.map(page => (
                    <PageNumber
                        key={page}
                        $active={page === meta.current_page}
                        onClick={() => handlePageChange(page)}
                    >
                        {page}
                    </PageNumber>
                ))}
                {endPage < meta.last_page && (
                    <>
                        {endPage < meta.last_page - 1 && <PageEllipsis>...</PageEllipsis>}
                        <PageNumber onClick={() => handlePageChange(meta.last_page)}>{meta.last_page}</PageNumber>
                    </>
                )}
                <PageButton
                    onClick={() => handlePageChange(meta.current_page + 1)}
                    disabled={meta.current_page === meta.last_page}
                >
                    {t('common.next')}
                </PageButton>
            </Pagination>
        );
    };

    return (
        <PageContainer>
            <PageHeader>
                <div>
                    <PageTitle>{t('RetailerBestOffers.title')}</PageTitle>
                    <Subtitle>{t('RetailerBestOffers.subtitle')}</Subtitle>
                </div>
            </PageHeader>
            <PageContent>
                {/* Filters Row */}
                <FiltersContainer>
                    <SearchWrapper>
                        <SearchIcon>
                            <FaSearch size={14} />
                        </SearchIcon>
                        <SearchInput
                            type="text"
                            placeholder={t('RetailerBestOffers.searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </SearchWrapper>
                    <FilterSelect>
                        <SearchableSelect
                            options={manufacturerOptions}
                            value={selectedManufacturer}
                            onChange={(_, __, option) => setSelectedManufacturer(option)}
                            placeholder={t('RetailerBestOffers.manufacturerFilter')}
                            isClearable
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                        />
                    </FilterSelect>
                    <FilterSelect>
                        <SearchableSelect
                            options={distributorOptions}
                            value={selectedDistributor}
                            onChange={(_, __, option) => setSelectedDistributor(option)}
                            placeholder={t('RetailerBestOffers.distributorFilter')}
                            isClearable
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                        />
                    </FilterSelect>
                    <FilterSelect $small>
                        <SearchableSelect
                            options={minDiscountOptions}
                            value={minDiscount}
                            onChange={(_, __, option) => setMinDiscount(option)}
                            placeholder={t('RetailerBestOffers.minDiscount')}
                            isClearable
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                        />
                    </FilterSelect>
                </FiltersContainer>

                {/* Sort Row */}
                <SortContainer>
                    <SortLabel>{t('RetailerBestOffers.sortBy')}:</SortLabel>
                    <SortButton
                        $active={sortBy === 'discount_value'}
                        onClick={() => handleSort('discount_value')}
                    >
                        {t('RetailerBestOffers.sortDiscount')}
                        {sortBy === 'discount_value' && (
                            sortDirection === 'desc' ? <FaSortAmountDown size={12} /> : <FaSortAmountUp size={12} />
                        )}
                    </SortButton>
                    <SortButton
                        $active={sortBy === 'end_at'}
                        onClick={() => handleSort('end_at')}
                    >
                        {t('RetailerBestOffers.sortDate')}
                        {sortBy === 'end_at' && (
                            sortDirection === 'desc' ? <FaSortAmountDown size={12} /> : <FaSortAmountUp size={12} />
                        )}
                    </SortButton>
                    <SortButton
                        $active={sortBy === 'product_name'}
                        onClick={() => handleSort('product_name')}
                    >
                        {t('RetailerBestOffers.sortName')}
                        {sortBy === 'product_name' && (
                            sortDirection === 'desc' ? <FaSortAmountDown size={12} /> : <FaSortAmountUp size={12} />
                        )}
                    </SortButton>
                    <ResultCount>
                        {meta.total} {t('RetailerBestOffers.results')}
                    </ResultCount>
                </SortContainer>

                {/* Content */}
                {loading ? (
                    <LoadingGrid>
                        {[...Array(8)].map((_, i) => (
                            <SkeletonCard key={i}>
                                <SkeletonImage />
                                <SkeletonContent>
                                    <SkeletonLine $width="80%" />
                                    <SkeletonLine $width="60%" />
                                    <SkeletonLine $width="40%" />
                                </SkeletonContent>
                            </SkeletonCard>
                        ))}
                    </LoadingGrid>
                ) : offers.length === 0 ? (
                    <Placeholder>{t('RetailerBestOffers.empty')}</Placeholder>
                ) : (
                    <>
                        <OffersGrid>
                            {offers.map((offer) => {
                                const ownerName = offer.owner_name || offer.distributor_name;
                                const discountPercent = formatDiscountPercent(offer.discount_percentage);
                                const { eurAmount, localAmount } = resolveDiscountedAmounts(offer);

                                const formattedDiscountedPrice = eurAmount !== null
                                    ? formatResolvedAmount({
                                        eurAmount,
                                        localAmount,
                                        localCurrencyId: offer.initial_price_currency_id,
                                    })
                                    : null;

                                const formattedInitialPrice = offer.initial_price
                                    ? formatResolvedAmount({
                                        eurAmount: offer.initial_price,
                                        localAmount: offer.initial_price_local,
                                        localCurrencyId: offer.initial_price_currency_id,
                                    })
                                    : null;

                                return (
                                    <OfferCard key={offer.id} onClick={() => setSelectedOffer(offer)}>
                                        {discountPercent && <DiscountBadge>-{discountPercent}</DiscountBadge>}
                                        <CardImageWrapper>
                                            {offer.image_url ? (
                                                <Image src={offer.image_url} alt={offer.product_name} />
                                            ) : (
                                                <ImagePlaceholder>{offer.product_name.charAt(0)}</ImagePlaceholder>
                                            )}
                                        </CardImageWrapper>
                                        <Content>
                                            <ProductName title={offer.product_name}>
                                                {truncate(offer.product_name, 35)}
                                            </ProductName>
                                            <MetaRow>
                                                {offer.manufacturer_name && (
                                                    <MetaTag>{offer.manufacturer_name}</MetaTag>
                                                )}
                                                {ownerName && (
                                                    <MetaTag $secondary>{ownerName}</MetaTag>
                                                )}
                                            </MetaRow>
                                            <CardFooter>
                                                <PriceSection>
                                                    {formattedDiscountedPrice && formattedInitialPrice ? (
                                                        <>
                                                            <NewPrice>{formattedDiscountedPrice}</NewPrice>
                                                            <OldPrice>{formattedInitialPrice}</OldPrice>
                                                        </>
                                                    ) : (
                                                        <DiscountOnly>
                                                            {formatResolvedAmount({
                                                                eurAmount: offer.discount_value,
                                                                localAmount: null,
                                                                localCurrencyId: offer.initial_price_currency_id,
                                                            })} off
                                                        </DiscountOnly>
                                                    )}
                                                </PriceSection>
                                                <ValidDate>
                                                    {t('RetailerBestOffers.validUntil', {
                                                        end: new Intl.DateTimeFormat().format(new Date(offer.end_at)),
                                                    })}
                                                </ValidDate>
                                            </CardFooter>
                                        </Content>
                                    </OfferCard>
                                );
                            })}
                        </OffersGrid>
                        {renderPagination()}
                    </>
                )}

                {/* Detail Modal */}
                {selectedOffer && (
                    <ModalOverlay onClick={() => setSelectedOffer(null)}>
                        <ModalContainer onClick={(e) => e.stopPropagation()}>
                            <ModalCloseButton onClick={() => setSelectedOffer(null)}>
                                <FaTimes size={16} />
                            </ModalCloseButton>
                            <ModalImageSection>
                                {selectedOffer.image_url ? (
                                    <ModalImage src={selectedOffer.image_url} alt={selectedOffer.product_name} />
                                ) : (
                                    <ModalImagePlaceholder>
                                        {selectedOffer.product_name.charAt(0)}
                                    </ModalImagePlaceholder>
                                )}
                                {formatDiscountPercent(selectedOffer.discount_percentage) && (
                                    <ModalDiscountBadge>
                                        -{formatDiscountPercent(selectedOffer.discount_percentage)}
                                    </ModalDiscountBadge>
                                )}
                            </ModalImageSection>
                            <ModalContent>
                                <ModalProductName>{selectedOffer.product_name}</ModalProductName>
                                {selectedOffer.description && (
                                    <ModalDescription>{selectedOffer.description}</ModalDescription>
                                )}
                                <ModalInfoGrid>
                                    {selectedOffer.manufacturer_name && (
                                        <ModalInfoItem>
                                            <FaBuilding size={14} />
                                            <span>{t('RetailerBestOffers.manufacturer')}</span>
                                            <strong>{selectedOffer.manufacturer_name}</strong>
                                        </ModalInfoItem>
                                    )}
                                    {(selectedOffer.owner_name || selectedOffer.distributor_name) && (
                                        <ModalInfoItem>
                                            <FaTruck size={14} />
                                            <span>{t('RetailerBestOffers.distributor')}</span>
                                            <strong>{selectedOffer.owner_name || selectedOffer.distributor_name}</strong>
                                        </ModalInfoItem>
                                    )}
                                    <ModalInfoItem>
                                        <FaCalendarAlt size={14} />
                                        <span>{t('RetailerBestOffers.validPeriod')}</span>
                                        <strong>
                                            {new Intl.DateTimeFormat().format(new Date(selectedOffer.start_at))} - {new Intl.DateTimeFormat().format(new Date(selectedOffer.end_at))}
                                        </strong>
                                    </ModalInfoItem>
                                </ModalInfoGrid>
                                <ModalPriceSection>
                                    {selectedOffer.initial_price ? (
                                        <>
                                            <ModalOldPrice>
                                                {formatResolvedAmount({
                                                    eurAmount: selectedOffer.initial_price,
                                                    localAmount: selectedOffer.initial_price_local,
                                                    localCurrencyId: selectedOffer.initial_price_currency_id,
                                                })}
                                            </ModalOldPrice>
                                            <ModalNewPrice>
                                                {formatResolvedAmount({
                                                    ...resolveDiscountedAmounts(selectedOffer),
                                                    localCurrencyId: selectedOffer.initial_price_currency_id,
                                                })}
                                            </ModalNewPrice>
                                            <ModalSavings>
                                                {t('RetailerBestOffers.youSave')} {formatResolvedAmount({
                                                    eurAmount: selectedOffer.discount_value,
                                                    localAmount: null,
                                                    localCurrencyId: selectedOffer.initial_price_currency_id,
                                                })}
                                            </ModalSavings>
                                        </>
                                    ) : (
                                        <ModalDiscountOnly>
                                            {formatResolvedAmount({
                                                eurAmount: selectedOffer.discount_value,
                                                localAmount: null,
                                                localCurrencyId: selectedOffer.initial_price_currency_id,
                                            })} {t('RetailerBestOffers.off')}
                                        </ModalDiscountOnly>
                                    )}
                                </ModalPriceSection>
                            </ModalContent>
                        </ModalContainer>
                    </ModalOverlay>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default RetailerBestOffers;

const truncate = (value, maxLength = 80) => {
    if (!value) return '';
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

const Subtitle = styled.p`
    margin: 0;
    color: #777;
`;

const FiltersContainer = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
`;

const SearchWrapper = styled.div`
    position: relative;
    flex: 1;
    min-width: 200px;
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #888;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 0.625rem 0.875rem 0.625rem 2.375rem;
    border: 1px solid rgba(130, 22, 146, 0.3);
    border-radius: 8px;
    font-size: 0.875rem;
    outline: none;

    &:focus {
        border-color: rgba(130, 22, 146, 0.6);
        box-shadow: 0 0 0 2px rgba(130, 22, 146, 0.1);
    }
`;

const FilterSelect = styled.div`
    min-width: ${({ $small }) => $small ? '120px' : '180px'};
`;

const SortContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
`;

const SortLabel = styled.span`
    color: #666;
    font-size: 0.875rem;
`;

const SortButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.875rem;
    border-radius: 999px;
    border: 1px solid ${({ $active }) => $active ? 'rgba(130, 22, 146, 0.6)' : 'rgba(130, 22, 146, 0.2)'};
    background: ${({ $active }) => $active ? 'rgba(130, 22, 146, 0.1)' : '#fff'};
    color: ${({ $active }) => $active ? 'rgba(130, 22, 146, 1)' : '#555'};
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(130, 22, 146, 0.08);
        border-color: rgba(130, 22, 146, 0.4);
    }
`;

const ResultCount = styled.span`
    margin-left: auto;
    color: #888;
    font-size: 0.875rem;
`;

const OffersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
`;

const OfferCard = styled.div`
    position: relative;
    border: 1px solid #eee;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: #fff;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }
`;

const DiscountBadge = styled.div`
    position: absolute;
    top: 8px;
    left: 8px;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    padding: 0.25rem 0.625rem;
    border-radius: 6px;
    font-size: 0.8125rem;
    font-weight: 700;
    z-index: 1;
`;

const CardImageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 120px;
    background: #f8f8f8;
`;

const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(130, 22, 146, 0.08) 0%, rgba(130, 22, 146, 0.04) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 600;
    color: rgba(130, 22, 146, 0.5);
`;

const Content = styled.div`
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
`;

const ProductName = styled.h4`
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.3;
    color: #333;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const MetaRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
`;

const MetaTag = styled.span`
    font-size: 0.6875rem;
    color: ${({ $secondary }) => $secondary ? '#888' : '#666'};
    background: ${({ $secondary }) => $secondary ? '#f5f5f5' : 'rgba(130, 22, 146, 0.06)'};
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100px;
`;

const CardFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: auto;
    padding-top: 0.5rem;
    border-top: 1px solid #f0f0f0;
`;

const PriceSection = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.375rem;
`;

const NewPrice = styled.span`
    font-size: 0.9375rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 700;
`;

const OldPrice = styled.span`
    font-size: 0.6875rem;
    color: #aaa;
    text-decoration: line-through;
`;

const DiscountOnly = styled.span`
    font-size: 0.8125rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
`;

const ValidDate = styled.span`
    font-size: 0.625rem;
    color: #999;
`;

const Placeholder = styled.div`
    padding: 2.5rem;
    text-align: center;
    border: 1px dashed rgba(130, 22, 146, 0.3);
    border-radius: 12px;
    color: #777;
`;

const LoadingGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
`;

const SkeletonCard = styled.div`
    border: 1px solid #eee;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
`;

const SkeletonImage = styled.div`
    width: 100%;
    height: 120px;
    background: linear-gradient(90deg, #f5f5f5 25%, #eee 50%, #f5f5f5 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;

    @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }
`;

const SkeletonContent = styled.div`
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SkeletonLine = styled.div`
    height: 12px;
    width: ${({ $width }) => $width || '100%'};
    background: linear-gradient(90deg, #f5f5f5 25%, #eee 50%, #f5f5f5 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
`;

const Pagination = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 2rem;
    flex-wrap: wrap;
`;

const PageButton = styled.button`
    padding: 0.5rem 1rem;
    border: 1px solid rgba(130, 22, 146, 0.3);
    border-radius: 8px;
    background: #fff;
    color: ${({ disabled }) => disabled ? '#ccc' : 'rgba(130, 22, 146, 1)'};
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: rgba(130, 22, 146, 0.05);
        border-color: rgba(130, 22, 146, 0.5);
    }
`;

const PageNumber = styled.button`
    width: 36px;
    height: 36px;
    border: 1px solid ${({ $active }) => $active ? 'rgba(130, 22, 146, 0.8)' : 'rgba(130, 22, 146, 0.2)'};
    border-radius: 8px;
    background: ${({ $active }) => $active ? 'rgba(130, 22, 146, 1)' : '#fff'};
    color: ${({ $active }) => $active ? '#fff' : '#555'};
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ $active }) => $active ? 'rgba(130, 22, 146, 0.9)' : 'rgba(130, 22, 146, 0.05)'};
    }
`;

const PageEllipsis = styled.span`
    padding: 0 0.25rem;
    color: #888;
`;

// Modal Styles
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const ModalContainer = styled.div`
    background: #fff;
    border-radius: 16px;
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    animation: slideUp 0.25s ease;

    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const ModalCloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    transition: background 0.2s;

    &:hover {
        background: #fff;
    }
`;

const ModalImageSection = styled.div`
    position: relative;
    width: 100%;
    height: 200px;
    background: #f8f8f8;
`;

const ModalImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ModalImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(130, 22, 146, 0.1) 0%, rgba(130, 22, 146, 0.05) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    font-weight: 600;
    color: rgba(130, 22, 146, 0.4);
`;

const ModalDiscountBadge = styled.div`
    position: absolute;
    top: 16px;
    left: 16px;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 1.125rem;
    font-weight: 700;
`;

const ModalContent = styled.div`
    padding: 1.5rem;
`;

const ModalProductName = styled.h2`
    margin: 0 0 0.75rem 0;
    font-size: 1.25rem;
    color: #333;
    line-height: 1.3;
`;

const ModalDescription = styled.p`
    color: #666;
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0 0 1rem 0;
`;

const ModalInfoGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
`;

const ModalInfoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;

    svg {
        color: rgba(130, 22, 146, 0.6);
        flex-shrink: 0;
    }

    span {
        color: #888;
    }

    strong {
        color: #333;
        margin-left: auto;
    }
`;

const ModalPriceSection = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    flex-wrap: wrap;
    padding-top: 1rem;
    border-top: 1px solid #eee;
`;

const ModalOldPrice = styled.span`
    font-size: 1.1rem;
    color: #999;
    text-decoration: line-through;
`;

const ModalNewPrice = styled.span`
    font-size: 1.75rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 700;
`;

const ModalSavings = styled.span`
    font-size: 0.875rem;
    color: #2e7d32;
    background: rgba(46, 125, 50, 0.1);
    padding: 0.25rem 0.625rem;
    border-radius: 4px;
`;

const ModalDiscountOnly = styled.span`
    font-size: 1.5rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 700;
`;
